import { accountTypes, accountTypeTitles, originalAccountTypes } from 'config/user.type.constants'

export const partiesMap: Record<string, string> = {
  creator: 'Loan Creator',
  loanProcessor: 'Loan Processor',
  loanOfficer: 'Loan Officer',
  broker: 'Broker',
  accountExecutive: 'Account Executive',
  underwriter: 'Underwriter',
  accountManager: 'Account Manager',
  appraiser: 'Appraiser',
}

const partyIdentity = (key: string) => key.replace(/_/g, '').toLowerCase()

/** The loan party slot an account type renames, when they are the same role. */
export const partySlotForAccountType = (accountTypeKey: string): string | undefined => {
  const identity = partyIdentity(accountTypeKey)
  return Object.keys(partiesMap).find((partyKey) => partyIdentity(partyKey) === identity)
}

/**
 * Party slots keep their own names (Loan Processor, Loan Officer). An in-use account type replaces
 * that name only when it was actually renamed in Manage Account Types. The stock title
 * ("Loan Processor - Third Party") is a different string and must not take over the party label.
 * Any other in-use account type is added as its own item. A deleted or unused type is absent.
 * A copied profile keeps its own item because it is a different role.
 */
export const partiesMapWithAccountTypes = (hasSuffix = false): Record<string, string> => {
  const result: Record<string, string> = { ...partiesMap }

  Object.keys(accountTypes).forEach((key) => {
    const slot = partySlotForAccountType(key)
    if (slot) {
      const current = accountTypes[key]
      if (current && current !== originalAccountTypes[key]) result[slot] = current
      return
    }
    result[key] = accountTypes[key]
  })

  if (hasSuffix) {
    Object.keys(result).forEach((key) => {
      result[key] = `${result[key]} [User]`
    })
  }
  return result
}

/**
 * The party record a Party View key reads and writes, by that record's own Type.
 * NOT the row's display name: a party slot is labeled with the matching user account type, and
 * "Loan Officer - Third Party" / "Valuation Reviewer" are account types, not party types, so a key
 * derived from the label points at a record that does not exist. An account type with no slot of
 * its own has only its own name to go by, which is what the backend falls back to as well.
 */
export const partyRecordName = (key: string): string => {
  const slot = Object.prototype.hasOwnProperty.call(partiesMap, key) ? key : partySlotForAccountType(key)
  if (slot) return partiesMap[slot]
  // An account type picked into Party View with no slot of its own: its ORIGINAL name, which is
  // what the backend hardcodes for closer / post_closer, so a tenant renaming the type does not
  // move the record its parties are filed under.
  return originalAccountTypes[key] || accountTypes[key] || partyViewKeyTitle(key)
}

/** True when this party is selected, including a selection still stored under the account-type key. */
export const partyViewIncludes = (selected: string[] | undefined, key: string): boolean => {
  if (!selected?.includes) return false
  if (selected.includes(key)) return true
  return selected.some((item) => partySlotForAccountType(item) === key)
}

/**
 * A party type that is an account type on the Manage Account Types selected (In Use) list.
 * Those names are renamed there, not with Party Custom Name.
 */
export const isManagedAccountParty = (type?: string | null): boolean => {
  if (!type) return false
  return Object.keys(accountTypes).some((key) => {
    if (accountTypes[key] === type || originalAccountTypes[key] === type) return true
    const slot = partySlotForAccountType(key)
    return !!slot && partiesMap[slot] === type
  })
}

/**
 * The current name of a built-in user type. Party groups still carry the original name
 * (Account Executive) after the user type is renamed (Business Development Manager).
 * A name that is not a built-in original is left as stored.
 */
export const accountTypeTitleForName = (name?: string | null): string => {
  if (!name) return ''
  const key = Object.keys(originalAccountTypes).find((accountKey) => originalAccountTypes[accountKey] === name)
  if (!key) return name
  return accountTypes[key] || accountTypeTitles[key] || name
}

/**
 * Label for a party type. A selected account type follows its Manage Account Types name.
 * Party Custom Name still applies to rolodex parties.
 */
export const partyTypeLabel = (
  party?: { CustomName?: string; Type?: string } | null,
  fallback = '',
): string => {
  const type = party?.Type || fallback
  if (isManagedAccountParty(type)) return accountTypeTitleForName(type)
  return accountTypeTitleForName(party?.CustomName || type || fallback)
}

/** Party group label, following a renamed user type when the stored name is still the original. */
export const partyDisplayName = (
  party?: { CustomName?: string; Type?: string } | null,
  fallback = '',
): string => partyTypeLabel(party, fallback)

/**
 * A Profile Party View entry that is a rolodex party (Escrow Company, Hazard Insurance Company),
 * not a user account type. Those keys are not in the user party map, so a check against that map
 * hides them.
 */
export const isRolodexPartyViewKey = (key: string, userParties: Record<string, string>): boolean => {
  if (!key) return false
  if (Object.prototype.hasOwnProperty.call(userParties, key)) return false
  if (partySlotForAccountType(key)) return false
  return true
}

/** "HazardInsuranceCompany" back to the party type title. */
export const partyViewKeyTitle = (key: string): string =>
  accountTypeTitleForName(
    key
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .trim(),
  )

/** The closing party for a Profile Party View key, including a key that differs only by case. */
export const findCloseParty = (
  closeParties: Record<string, any> | undefined,
  key: string,
): { id: string; party: any } | undefined => {
  if (!closeParties || !key) return undefined
  if (closeParties[key]) return { id: key, party: closeParties[key] }
  const title = partyViewKeyTitle(key)
  const match = Object.keys(closeParties).find((partyKey) => {
    if (partyKey.toLowerCase() === key.toLowerCase()) return true
    const party = closeParties[partyKey]
    return party?.Type === title || party?.CustomName === title
  })
  if (!match) return undefined
  return { id: match, party: closeParties[match] }
}

/** Checklist value with renamed account types folded onto their party slot. */
export const partyViewSelectionForOptions = (selected: string[] = []): string[] => {
  const next: string[] = []
  selected.forEach((key) => {
    const slot = partySlotForAccountType(key) || key
    if (!next.includes(slot)) next.push(slot)
  })
  return next
}
